/**
*
* PriceDifferenceDialog
*
*/

import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import Text from 'ui/Text';
import { colors } from 'ui/constants';
import FormattedPrice from 'ui/FormattedPrice';
import { totalPriceSelector, itemListSelector } from 'domain/Options/itemList';
import { countrySelector, currencySelector } from 'domain/LanguageProvider/selectors';
import { priceDifferenceDialogSelector, ipexGalleryModeSelector } from 'Application/selectors';
import { hidePriceDifferenceDialog } from 'Application/actions';
import messages from './messages';
import Description from './Description';
import { KompisPrompt, KompisText } from '@inter-ikea-kompis/react-components';
import { PromptButtonTypeEnum } from '@inter-ikea-kompis/component-prompt';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';

const Prices = styled.div`
  display: flex;
  padding-bottom: 24px;
`;

const Price = styled.div`
  width: 50%;
`;

const PaddedText = styled(Text)`
  padding: 4px 0;
`;

function PriceDifferenceDialog({ dialogState: { visible, oldPrice, oldFamilyPrice, oldCountry, oldCurrency }, onDismiss, currentPrice, currentPriceInclFamilyPrice, currentCountry, currentCurrency, itemList }, cwcalcReachedFromIpexGallery) {
  
  let shoppingProducts = [];
  itemList.map(object => {
    shoppingProducts.push({
      type: object.getIn(['item', 'ItemType']),
      id: object.getIn(['item', 'ItemNo']),
      quantity: object.get('amount')
    })
  });

  const { formatMessage } = useIntl();
  const showCountryChanged = currentCountry !== oldCountry || currentCurrency !== oldCurrency;
  const currencyChanged = currentCurrency !== oldCurrency;
  const title = showCountryChanged ? messages.newPriceTitle : messages.headsUp;
  const subtitle = showCountryChanged ? messages.newCountrySubtitle : null;
  const message = showCountryChanged ? messages.newCountryMessage : messages.priceDifferenceMessage;
  const aboveOld = showCountryChanged ? `IKEA (${oldCountry})` : [messages.oldPrice, ':'];
  const aboveCurrent = showCountryChanged ? `IKEA (${currentCountry})` : [messages.newPrice, ':'];

  return (
    <KompisPrompt
      heading={formatMessage({
        id: title.id, defaultMessage: title.defaultMessage
      })}
      primaryButtonLabel={formatMessage({
        id: messages.priceDifferenceOkButton.id, defaultMessage: messages.priceDifferenceOkButton.defaultMessage
      })}
      onButtonClick={event => {
        onDismiss();
        if (event.detail.type === PromptButtonTypeEnum.primaryButton) {
          // onContinueClick();
        } else {
          // onBackClick();
        }
      }}
      visible={visible}
      theme={SkapaTheme}
      disableIframeSticky={cwcalcReachedFromIpexGallery}
    >
      {subtitle &&
        <Description text={formatMessage({ id: subtitle.id, defaultMessage: subtitle.defaultMessage })} />
      }
      <Description text={formatMessage({id: message.id, defaultMessage: message.defaultMessage})} />
      <Prices>
        <Price>
          <PaddedText text={aboveOld} />
          <KompisText theme={SkapaTheme} type={ThemeFontStyleTypeEnum.bodyMedium} bold={true} >
            <FormattedPrice layout={'rowPrice'} strikethrough bold value={oldPrice} overrideCurrency={currencyChanged ? oldCurrency : null} shoppingProducts={shoppingProducts}/>
            {oldFamilyPrice ? <FormattedPrice strikethrough bold value={oldFamilyPrice} overrideCurrency={currencyChanged ? oldCurrency : null} /> : null}
          </KompisText>
        </Price>
        <Price>
          <PaddedText color={colors.blue} text={aboveCurrent} />
          <KompisText theme={SkapaTheme} type={ThemeFontStyleTypeEnum.bodyMedium} bold={true} >
           <FormattedPrice layout={'rowPrice'} bold value={currentPrice} disabled={!currentPrice} shoppingProducts={shoppingProducts}/>
           {currentPriceInclFamilyPrice ? <FormattedPrice bold value={currentPriceInclFamilyPrice} disabled={!currentPriceInclFamilyPrice} /> : null}
          </KompisText>
        </Price>
      </Prices>
    </KompisPrompt>)
}

PriceDifferenceDialog.propTypes = {
  dialogState: PropTypes.object.isRequired,
  onDismiss: PropTypes.func.isRequired,
  currentPrice: PropTypes.number,
  currentCountry: PropTypes.string.isRequired,
  currentCurrency: PropTypes.string.isRequired,
};

PriceDifferenceDialog.defaultProps = {
  currentPrice: null,
};

const mapStateToProps = createStructuredSelector({
  dialogState: priceDifferenceDialogSelector,
  currentPrice: totalPriceSelector,
  currentCountry: countrySelector,
  currentCurrency: currencySelector,
  itemList: itemListSelector,
  cwcalcReachedFromIpexGallery: ipexGalleryModeSelector,
});

function mapDispatchToProps(dispatch) {
  return {
    onDismiss: () => {
      dispatch(hidePriceDifferenceDialog());
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(PriceDifferenceDialog);
